import { useCallback, useEffect, useRef, useState, useMemo, MutableRefObject } from 'react'

import { Container, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import lottie, { AnimationItem } from 'lottie-web'
import Image from 'next/image'
import { toast } from 'react-toastify'
import { useRouter } from 'next/router'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'

import DownArrow from 'public/images/arrow-down.svg'
import Seed from '@/components/atoms/Seed'
import WishItem from '@/components/molecules/WishItem'
import Milestone from '@/components/molecules/Milestone'
import Footer from '@/components/organisms/Footer'
import Header from '@/components/organisms/Header'
import { useGetPlantTypes } from '@/services/plant-type/hooks'
import { useGetCurrentMilestone, useGetMilestones, useGetReachedMilestones } from '@/services/milestone/hooks'
import { strapiLoader } from '@/services/strapi'
import NextArrow from 'public/images/next-arrow.svg'
import PrevArrow from 'public/images/prev-arrow.svg'
import WishInterface from '@/model/interfaces/wish'
import PlantTypeInterface from '@/model/interfaces/plant-type'
import sparks from 'public/animations/sparks.json'
import bean_animation from 'public/animations/bean_animation.json'
import lentill_animation from 'public/animations/lentill_animation.json'
import pearmillet_animation from 'public/animations/pearmillet_animation.json'
import peas_animation from 'public/animations/peas_animation.json'
import shorgum_animation from 'public/animations/shorgum_animation.json'
import Spinner from '@/components/atoms/Spinner'
import routes from '@/config/routes'
import { isObjectiveCompleted } from '@/utils/index'
import { getWishes } from '@/services/wish/api'
import MilestoneInterface from '@/model/interfaces/milestone'
import useWindowSize from '@/hooks/useWindowSize'
import useDebounce from '@/hooks/useDebounce'

import * as S from './styled'

const PLANT_TYPES_PARAMS = '?populate[0]=icon&populate[1]=image&populate[2]=animation'
const NEXT_MILESTONES_PARAMS =
  '?populate[0]=wish.user_info&fields[0]=name&fields[1]=distance&fields[2]=reached&pagination[pageSize]=1&sort[0]=distance:asc&filters[reached][$eq]=false'
const REACHED_MILESTONES_PARAMS =
  '?populate[0]=wish.user_info&fields[0]=name&fields[1]=distance&fields[2]=reached&pagination[pageSize]=100&sort[0]=distance:asc&filters[reached][$eq]=true'

const POPULATE = 'populate[0]=plant_type.image&populate[1]=plant_type.icon&populate[2]=milestone&populate[3]=user_info'
const LIMIT = 10

const getAnimationBySlug = (slug: string) => {
  switch (slug) {
    case 'bean':
      return bean_animation
    case 'lentils':
      return lentill_animation
    case 'pearl-millet':
      return pearmillet_animation
    case 'peas':
      return peas_animation
    case 'sorghum':
      return shorgum_animation
    default:
      return bean_animation
  }
}

type Props = {
  wishSingle: WishInterface | undefined
}

const Wishes = ({ wishSingle }: Props) => {
  const { t } = useTranslation()
  const { locale, push, query } = useRouter()
  const size = useWindowSize()
  const debouncedSize = useDebounce(size, 500)
  const scrollRef = useRef<HTMLDivElement>(null)

  const refs1 = useRef<HTMLDivElement[]>([])
  refs1.current = []

  const refs2 = useRef<HTMLDivElement[]>([])
  refs2.current = []

  const [activeWish, setActiveWish] = useState<WishInterface | undefined>(undefined)
  const [sparkAnimation, setSparkAnimation] = useState<AnimationItem | null>(null)
  const [wishes, setWishes] = useState<WishInterface[]>([])
  const [animated, setAnimated] = useState(false)
  const [activeList, setActiveList] = useState(1)
  const [width, setWidth] = useState(0)
  const [isResizing, setIsResizing] = useState(false)

  const { data: plants } = useGetPlantTypes(PLANT_TYPES_PARAMS)
  const { data: milestones } = useGetMilestones(NEXT_MILESTONES_PARAMS)
  const { data: reachedMilestones } = useGetReachedMilestones(REACHED_MILESTONES_PARAMS)
  const { data: currentMilestone } = useGetCurrentMilestone()

  const isCompleted = currentMilestone ? isObjectiveCompleted(currentMilestone) : false
  const isLoading = !activeWish || !wishes

  let isLastWish = false
  if (wishes && activeWish) {
    const index = wishes.findIndex(o => o.id === activeWish?.id)
    if (index >= wishes.length - 1) {
      isLastWish = true
    }
  }

  // Carga inicial
  useEffect(() => {
    if (wishSingle?.id) {
      loadData(String(wishSingle.id))
    } else {
      loadLastData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wishSingle?.id])

  // Carga tras cambiar de wish
  useEffect(() => {
    if (query.wishId) {
      if (
        (activeWish && String(activeWish?.id) !== query.wishId) ||
        (!activeWish && String(wishSingle?.id) !== query.wishId)
      ) {
        const fetchData = async () => {
          const id = String(query.wishId)

          const paramsNextWishes = `?filters[id][$gt]=${id}&sort[0]=id%3Aasc&pagination[limit]=${LIMIT}&locale=${locale}&${POPULATE}`
          const { data: nextWishes } = await getWishes(paramsNextWishes)

          const paramsCurrentWish = `/${id}?${POPULATE}`
          const { data: currentWish } = await getWishes(paramsCurrentWish)

          const paramsPreviusWishes = `?filters[id][$lt]=${id}&sort[0]=id%3Adesc&pagination[limit]=${LIMIT}&locale=${locale}&${POPULATE}`
          const { data: previusWishes } = await getWishes(paramsPreviusWishes)

          let newWishes: WishInterface[] = [
            ...wishes,
            ...previusWishes.reverse(),
            currentWish,
            ...nextWishes,
          ] as WishInterface[]

          newWishes = orderBy(newWishes, 'id', 'asc')
          newWishes = uniqBy(newWishes, 'id')

          setWishes(newWishes)
          setActiveWish(currentWish)

          if (activeList === 1) {
            setActiveList(2)
          } else {
            setActiveList(1)
          }
        }

        fetchData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.wishId])

  useEffect(() => {
    if (sparkAnimation && activeWish) {
      sparkAnimation.stop()
      setTimeout(() => sparkAnimation.play(), 400)
    } else if (!sparkAnimation) {
      const container = document.querySelector(`#sparks-animation`)

      if (container) {
        const anim = lottie.loadAnimation({
          container,
          animationData: sparks,
          renderer: 'svg',
          loop: true,
          autoplay: false,
        })

        anim.stop()
        setSparkAnimation(anim)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWish])

  useEffect(() => {
    if (wishes && activeWish) {
      const element1 = refs1.current.filter(el => el.id === `reference-${activeWish.id}`)[0]
      element1?.scrollIntoView({ inline: 'center', block: 'end' /*, behavior: 'smooth'*/ })

      const element2 = refs2.current.filter(el => el.id === `reference-${activeWish.id}`)[0]
      element2?.scrollIntoView({ inline: 'center', block: 'end' /*, behavior: 'smooth'*/ })

      setTimeout(() => setAnimated(false), 50)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWish, activeWish?.id])

  useEffect(() => {
    if (plants?.length > 0) {
      loadAnimations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plants])

  useEffect(() => {
    if (debouncedSize && debouncedSize.width) {
      setWidth(debouncedSize.width)

      if (width && debouncedSize.width !== width) {
        const index = wishes.findIndex(o => o.id === activeWish?.id)
        if (wishes[index - 1]) {
          handleClickPlant(wishes[index - 1])
        } else if (wishes[index + 1]) {
          handleClickPlant(wishes[index + 1])
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSize])

  useEffect(() => {
    const flag = debouncedSize && debouncedSize.width !== size.width
    if (isResizing !== flag) setIsResizing(flag)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSize, size])

  const loadData = useCallback(
    async (id: string) => {
      try {
        const paramsNextWishes = `?filters[id][$gt]=${id}&sort[0]=id%3Aasc&pagination[limit]=${LIMIT}&locale=${locale}&${POPULATE}`
        const { data: nextWishes } = await getWishes(paramsNextWishes)

        const paramsCurrentWish = `/${id}?${POPULATE}`
        const { data: currentWish } = await getWishes(paramsCurrentWish)

        const paramsPreviusWishes = `?filters[id][$lt]=${id}&sort[0]=id%3Adesc&pagination[limit]=${LIMIT}&locale=${locale}&${POPULATE}`
        const { data: previusWishes } = await getWishes(paramsPreviusWishes)

        const newWishes: WishInterface[] = [...previusWishes.reverse(), currentWish, ...nextWishes] as WishInterface[]

        setWishes(newWishes)
        setActiveWish(currentWish)
      } catch (error) {
        setAnimated(false)
      }
    },
    [locale]
  )

  const loadLastData = useCallback(async () => {
    try {
      const param = `?sort[0]=id%3Adesc&pagination[limit]=1&${POPULATE}`
      const { data: lastWishes } = await getWishes(param)

      if (activeWish?.id !== lastWishes[0]?.id) {
        gotoWish(lastWishes[0]?.id)
      } else {
        setAnimated(false)
      }
    } catch (error) {
      setAnimated(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWish])

  const loadAnimations = () => {
    plants?.map((plant: PlantTypeInterface) => {
      const plantSlug = plant.attributes?.slug
      initAnimation(plantSlug)
    })
  }

  const initAnimation = (slug: string) => {
    const container = document.querySelector(`#animation-${slug}`) || null
    const animationData = getAnimationBySlug(slug)

    if (container) {
      lottie.loadAnimation({
        container,
        animationData,
        renderer: 'svg',
        loop: true,
        autoplay: true,
      })
    }
  }

  const addToRefs1 = (el: HTMLDivElement) => {
    if (el && !refs1.current.includes(el)) {
      refs1.current.push(el)
    }
  }

  const addToRefs2 = (el: HTMLDivElement) => {
    if (el && !refs2.current.includes(el)) {
      refs2.current.push(el)
    }
  }

  const gotoWish = (wishId: number) => {
    setAnimated(true)

    push(
      {
        pathname: '/wishes',
        query: { wishId },
      },
      undefined,
      { shallow: true }
    )
  }

  const handleClickPlant = (wish: WishInterface | undefined) => {
    if (wish && wish?.id !== activeWish?.id) {
      gotoWish(wish?.id)

      const element1 = refs1.current.filter(el => el.id === `reference-${wish?.id}`)[0]
      element1?.scrollIntoView({ inline: 'center', block: 'end', behavior: 'smooth' })

      const element2 = refs2.current.filter(el => el.id === `reference-${wish?.id}`)[0]
      element2?.scrollIntoView({ inline: 'center', block: 'end', behavior: 'smooth' })
    }
  }

  const handleClickNextMilestone = () => {
    if (activeWish && reachedMilestones?.length > 0) {
      const haveNextMilestone = reachedMilestones
        ? reachedMilestones?.filter(
            milestone => milestone?.attributes?.wish && milestone?.attributes?.wish?.data?.id > activeWish?.id
          )
        : []
      if (haveNextMilestone?.length > 0) {
        const nextMilestoneId = haveNextMilestone?.[0]?.attributes?.wish?.data?.id
        nextMilestoneId && gotoWish(nextMilestoneId)
      } else {
        handleClickLastWish()
      }
    } else {
      handleClickLastWish()
    }
  }

  const handleClickPrevMilestone = () => {
    if (activeWish && reachedMilestones) {
      const havePrevMilestone = reachedMilestones
        ? reachedMilestones?.filter(
            milestone => milestone?.attributes?.wish && milestone?.attributes?.wish?.data?.id < activeWish?.id
          )
        : []
      if (havePrevMilestone?.length > 0) {
        const prevMilestoneId = havePrevMilestone?.[havePrevMilestone.length - 1]?.attributes?.wish?.data?.id
        prevMilestoneId && gotoWish(prevMilestoneId)
      }
    }
  }

  const handleClickLastWish = () => {
    setAnimated(true)
    loadLastData()
  }

  const handleClickShare = () => {
    const origin = typeof window !== 'undefined' && window.location.origin ? window.location.origin : ''
    const shareUrl = `${origin}${routes.wishes}?wishId=${activeWish?.id}`

    try {
      navigator.clipboard.writeText(shareUrl)
      toast.success(t('Url copied to clipboard'))
    } catch {
      toast.error(t('An error has occurred'))
    }
  }

  const handleNextPlant = () => {
    const currentId = activeWish?.id
    const nextIndex = wishes.findIndex(wish => wish.id === currentId) + 1
    const wishSelected = wishes[nextIndex] || null

    if (wishSelected) {
      handleClickPlant(wishSelected)
    }
  }
  const handlePrevPlant = () => {
    const currentId = activeWish?.id
    const prevIndex = wishes.findIndex(wish => wish.id === currentId) - 1
    const wishSelected = wishes[prevIndex] || null

    if (wishSelected) {
      handleClickPlant(wishSelected)
    }
  }

  return (
    <S.ComponentStyled isLastWish={isLastWish}>
      <Header showBack showBtn={!isCompleted} />
      <div className='global-page-wrapper'>
        <div className='title-container'>
          <Container maxWidth='xl'>
            <Grid container justifyContent='center'>
              <Grid item xs={12} md={4}>
                <h1 className='page-title'>{t('plant your wish for the future')}</h1>
                <div className='last-wish-copy-wrapper'>
                  {!isLoading && !isResizing && (
                    <span className='last-wish-copy' onClick={handleClickLastWish}>
                      {t('Last wish')}
                      <DownArrow className='arrow' />
                    </span>
                  )}
                </div>
              </Grid>
            </Grid>
          </Container>
        </div>

        {isResizing && (
          <S.Loading>
            <Spinner />
          </S.Loading>
        )}

        <div className='bottom-plants' style={{ opacity: isResizing ? 0 : 1 }}>
          {activeWish &&
            reachedMilestones &&
            reachedMilestones?.[0]?.attributes?.wish &&
            activeWish?.id > reachedMilestones?.[0]?.attributes?.wish?.data?.id && (
              <p className='milestone-control left' onClick={handleClickPrevMilestone}>
                <PrevArrow className='arrow' />
                {t('Previous milestone')}
              </p>
            )}
          {activeWish && !isLastWish && (
            <p className='milestone-control center' onClick={handleClickLastWish}>
              {t('Last wish')}
            </p>
          )}
          {activeWish && activeWish?.id !== wishes?.[wishes.length - 1]?.id && (
            <p className='milestone-control right' onClick={handleClickNextMilestone}>
              {t('Next milestone')}
              <NextArrow className='arrow' />
            </p>
          )}

          <S.WishScrollContainer
            isActiveSlug={
              activeWish && !animated ? activeWish?.attributes?.plant_type?.data?.attributes?.slug : undefined
            }
            className='scroll-container'
            ref={scrollRef}
            hidePlants={!activeWish || !wishes}
          >
            <div className='howitworks-background' />
            <div id={'sparks-animation'} />
            <div id={'animation-bean'} className={'plant-animation-container'} />
            <div id={'animation-lentils'} className={'plant-animation-container'} />
            <div id={'animation-pearl-millet'} className={'plant-animation-container'} />
            <div id={'animation-peas'} className={'plant-animation-container'} />
            <div id={'animation-sorghum'} className={'plant-animation-container'} />

            {isLoading && (
              <S.Loading>
                <Spinner />
              </S.Loading>
            )}
            <div className='plants-container'>
              <div
                className='plants'
                style={{
                  position: 'absolute',
                  visibility: activeList === 1 && !isResizing ? 'visible' : 'hidden',
                }}
              >
                <S.LimitScroll />
                <WishesList
                  wishes={wishes}
                  handleClickPlant={handleClickPlant}
                  activeWish={activeWish}
                  refs={refs1}
                  animated={animated}
                  addToRefs={addToRefs1}
                  isLoading={isLoading}
                  handleClickShare={handleClickShare}
                  reachedMilestones={reachedMilestones}
                  handleNextPlant={handleNextPlant}
                  handlePrevPlant={handlePrevPlant}
                />

                {!isLoading && <MilestonesList milestones={milestones} animated={animated} />}
              </div>
              <div
                className='plants'
                style={{
                  position: 'absolute',
                  visibility: activeList !== 1 && !isResizing ? 'visible' : 'hidden',
                }}
              >
                <S.LimitScroll />
                <WishesList
                  wishes={wishes}
                  handleClickPlant={handleClickPlant}
                  activeWish={activeWish}
                  refs={refs2}
                  animated={animated}
                  addToRefs={addToRefs2}
                  isLoading={isLoading}
                  handleClickShare={handleClickShare}
                  reachedMilestones={reachedMilestones}
                  handleNextPlant={handleNextPlant}
                  handlePrevPlant={handlePrevPlant}
                />

                {!isLoading && <MilestonesList milestones={milestones} animated={animated} />}
              </div>
            </div>
          </S.WishScrollContainer>
        </div>
      </div>
      <Footer />
    </S.ComponentStyled>
  )
}

const WishesList = ({
  wishes,
  handleClickPlant,
  activeWish,
  refs,
  animated,
  addToRefs,
  isLoading,
  handleClickShare,
  reachedMilestones,
  handleNextPlant,
  handlePrevPlant,
}: WishesListProps) => {
  const { t } = useTranslation()

  return (
    <>
      {wishes?.map((wish, i) => {
        const plantImg = wish?.attributes?.plant_type?.data?.attributes?.image?.data?.attributes?.url
        const plantTitle = wish?.attributes?.plant_type?.data?.attributes?.title
        const wishIcon = wish?.attributes?.plant_type?.data?.attributes?.icon?.data?.attributes?.url
        const findMilestone =
          reachedMilestones &&
          reachedMilestones?.length > 0 &&
          reachedMilestones?.filter(
            (milestone: MilestoneInterface) => milestone?.attributes?.wish?.data?.id === wish?.id
          )?.[0]
        const userImage = wish?.attributes?.user_info?.image_url
        const isActiveWish = activeWish?.id === wish?.id

        return (
          <div
            key={wish?.id}
            className={`plant-wrapper ${isActiveWish && 'active'}`}
            onClick={() => handleClickPlant(wish, refs.current[i])}
            ref={addToRefs}
            id={`reference-${wish?.id}`}
          >
            {!isLoading && findMilestone && (
              <div className='milestone'>
                <Milestone
                  active
                  title={findMilestone?.attributes?.name}
                  target={findMilestone?.attributes?.distance}
                  userImage={userImage}
                />
              </div>
            )}
            {isActiveWish && (
              <div className='wish-wrapper' style={{ opacity: animated ? 0 : 1 }}>
                {activeWish && activeWish?.id !== wishes?.[0]?.id && (
                  <p className='milestone-control left' onClick={() => handlePrevPlant()}>
                    <PrevArrow className='arrow' />
                    {t('Previous')}
                  </p>
                )}
                <div className='wish-item-wrapper'>
                  <WishItem
                    onShare={handleClickShare}
                    wishId={activeWish?.id}
                    username={wish.attributes?.user_info?.name || ''}
                    avatar={wish.attributes?.user_info?.image_url}
                    comment={wish.attributes?.text || ''}
                    location={wish.attributes?.user_info?.location || ''}
                  />
                  {wishIcon && (
                    <div className='seed'>
                      <Seed url={wishIcon} title={plantTitle} />
                    </div>
                  )}
                </div>
                {activeWish && activeWish?.id !== wishes?.[wishes.length - 1]?.id && (
                  <p className='milestone-control right' onClick={() => handleNextPlant()}>
                    {t('Next')}
                    <NextArrow className='arrow' />
                  </p>
                )}
              </div>
            )}
            <S.PlantImage className={'plant-img'} isActiveWish={activeWish?.id === wish.id} animated={animated}>
              {plantImg && <PlantImg src={plantImg} />}
            </S.PlantImage>
          </div>
        )
      })}
    </>
  )
}

type WishesListProps = {
  wishes: WishInterface[]
  handleClickPlant: (wish: WishInterface | undefined, ref: HTMLDivElement) => void
  activeWish: WishInterface | undefined
  refs: MutableRefObject<HTMLDivElement[]>
  animated: boolean
  addToRefs: (el: HTMLDivElement) => void
  isLoading: boolean
  handleClickShare: () => void
  reachedMilestones: MilestoneInterface[] | undefined
  handleNextPlant: () => void | undefined
  handlePrevPlant: () => void | undefined
}

const MilestonesList = ({ milestones }: MilestonesListProps) => {
  const childOne = useMemo(
    () => (
      <>
        {milestones.map(milestone => (
          <div key={milestone.id} className='next-milestone'>
            <div className='next-milestone-wrapper'>
              <Milestone target={milestone.attributes?.distance} title={milestone?.attributes?.name} />
            </div>
          </div>
        ))}
      </>
    ),
    [milestones]
  )
  return <>{childOne}</>
}

type MilestonesListProps = {
  milestones: MilestoneInterface[]
  animated: boolean
}

const PlantImg = ({ src }: PlantImgProps) => {
  const childOne = useMemo(
    () => (
      <div className='plant-image-container'>
        <Image loader={strapiLoader} src={src} alt={' '} layout='fill' objectFit='contain' />
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  return <>{childOne}</>
}

type PlantImgProps = {
  src: string
}

export default Wishes
